import { useEffect, useState } from 'react';
import { CheckIcon, CopyIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { useDraft } from '../builder/useDraftContext';

/**
 * The current draft, read as a definition (FR-055).
 *
 * **Unsaved edits included** — that is the point. Someone opens this to check
 * what they have built before committing it, and a preview of the last *saved*
 * version would answer a question nobody asked.
 *
 * Read-only, with a copy action. It is not an editor: the canvas is, and a
 * second editable representation of one document is two places for the truth
 * to live.
 *
 * The `yaml` package loads only when this opens. It is a parser nobody needs
 * on any other route, and the bundle gate measures the chunk it would otherwise
 * sit in (research D13).
 */
export function ContractPreviewDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { draft } = useDraft();
  const [text, setText] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    void import('../builder/preview/toYaml').then((module) => {
      // The dialog may have closed while the chunk was in flight; setting state
      // then would be work nobody is waiting for.
      if (!cancelled) setText(module.toYamlPreview(draft));
    });

    return () => {
      cancelled = true;
    };
  }, [open, draft]);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = () => {
    if (!text) return;
    void navigator.clipboard.writeText(text).then(() => setCopied(true));
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) {
          setText(null);
          setCopied(false);
        }
      }}
    >
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Definition preview</DialogTitle>
          <DialogDescription>
            This workflow as it stands, including changes you have not saved.
            The arrangement of the canvas is not part of the definition and is
            not shown.
          </DialogDescription>
        </DialogHeader>

        <pre
          data-testid="contract-preview"
          // `tabIndex` so a keyboard user can scroll it; `readOnly` is implicit
          // in `pre`, and a textarea would invite editing that goes nowhere.
          tabIndex={0}
          aria-label="Workflow definition"
          className="bg-muted text-foreground font-brand-mono max-h-[50vh] overflow-auto rounded-md p-3 text-xs whitespace-pre"
        >
          {text ?? 'Preparing…'}
        </pre>

        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>Close</DialogClose>
          <Button onClick={copy} disabled={!text}>
            {copied ? (
              <CheckIcon aria-hidden="true" />
            ) : (
              <CopyIcon aria-hidden="true" />
            )}
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
